import type { ApprovalRequest } from '@/lib/types';
import { formatPKR } from '../format';
import { BucketShell } from './_BucketShell';
import { PostedView } from './PostedView';
import { readStructuredFields } from './_payload';

export interface PostedServiceViewProps {
  req: ApprovalRequest;
}

export function PostedServiceView({ req }: PostedServiceViewProps) {
  const sf = readStructuredFields(req);
  const preflight = (sf['service_preflight'] ?? sf['servicePreflight']) as
    | Record<string, unknown>
    | undefined;
  const lines = Array.isArray(preflight?.['service_lines'])
    ? (preflight['service_lines'] as Record<string, unknown>[])
    : [];
  const taxChecked = preflight ? readTaxStatus(preflight['stax_checked']) : '—';

  return (
    <BucketShell req={req}>
      <PostedView req={req} embedded />
      {preflight && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-0 p-16">
          <div className="flex flex-wrap items-start justify-between gap-12">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                Service invoice details
              </p>
              <p className="mt-4 text-sm text-neutral-700">
                Posted with invoice-level tax {taxChecked.toLowerCase()}.
              </p>
            </div>
            <div className="text-right text-sm font-medium text-neutral-900">
              <p>{formatWholePKR(preflight['gross_amount'])}</p>
              <p className="text-xs font-normal uppercase tracking-wide text-neutral-500">
                Gross amount
              </p>
            </div>
          </div>
          <div className="mt-12 grid gap-12 tablet:grid-cols-3">
            <Readout label="Service book" value={readText(preflight['service_book_name'])} />
            <Readout label="Supplier invoice ref" value={readText(preflight['supplier_invoice_ref'])} />
            <Readout label="Taxable amount" value={formatWholePKR(preflight['taxable_amount'])} />
          </div>
          {lines.length > 0 && (
            <div className="mt-16 overflow-x-auto rounded-md border border-neutral-200">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-12 py-8 text-left font-semibold">Description</th>
                    <th className="px-12 py-8 text-left font-semibold">Account code</th>
                    <th className="px-12 py-8 text-right font-semibold">Amount</th>
                    <th className="px-12 py-8 text-right font-semibold">Taxable</th>
                    <th className="px-12 py-8 text-right font-semibold">Tax</th>
                    <th className="px-12 py-8 text-left font-semibold">Tax status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-neutral-0">
                  {lines.map((line, index) => (
                    <tr key={`${readText(line['description'])}-${index}`}>
                      <td className="max-w-[320px] px-12 py-10 text-neutral-900">
                        {readText(line['description'])}
                      </td>
                      <td className="px-12 py-10 text-neutral-700">
                        <div>{readText(line['code_combination_value'])}</div>
                        <div className="mt-2 text-xs text-neutral-500">
                          {readText(line['code_combination_desc'])}
                        </div>
                      </td>
                      <td className="px-12 py-10 text-right font-medium text-neutral-900">
                        {formatWholePKR(line['amount'])}
                      </td>
                      <td className="px-12 py-10 text-right text-neutral-700">
                        {formatWholePKR(line['taxable_amount'])}
                      </td>
                      <td className="px-12 py-10 text-right text-neutral-700">
                        {formatWholePKR(line['tax_amount'])}
                      </td>
                      <td className="px-12 py-10 text-neutral-700">
                        {readTaxStatus(line['stax_checked'])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </BucketShell>
  );
}

function readText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function readTaxStatus(value: unknown): string {
  return value === true ? 'Checked' : value === false ? 'Unchecked' : '—';
}

function formatWholePKR(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return formatPKR(Math.ceil(n));
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-12">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-4 truncate text-sm font-medium text-neutral-900" title={value}>
        {value || '—'}
      </p>
    </div>
  );
}
